import { TZDate } from '@repo/tzdate';
import {
    CartItem,
    MembershipIntentMultiResponse,
    MembershipSite,
    Package,
    Product,
    ProductInstance,
    TicketOptionWithQuantity,
    Timeslot,
} from '@repo/types';
import { getTicketOptionsWithPaymentPlans } from '../booking/createIntent';
import { minBy } from 'lodash-es';
import { debugLog } from '@repo/common-utils/Logger';

export function getCartItemId(item: CartItem) {
    if (item.pkg) {
        return (
            (item.idPrefix ?? '') +
            (item.pkg?.pkg.id ?? '') +
            '-' +
            (item.pkg?.date?.format() ?? '')
        );
    }
    return (
        (item.idPrefix ?? '') +
        item.products[0]?.product?.id +
        '-' +
        item.products.map((x) => x.id).join('-')
    );
}

export function getCartItemSummaryDateInformation(cartItem: CartItem) {
    if (cartItem.pkg) {
        const [start, end] = getPackageDuration(
            cartItem.pkg.date,
            cartItem.pkg.pkg,
            cartItem.products,
        );
        return start.format('ll') + ' - ' + end.format('ll');
    } else if (cartItem.products.length > 1) {
        const productInstances = cartItem.products;
        const lastIndex = productInstances.length - 1;
        const secondLastIndex = productInstances.length - 2;
        const lastProductInstance = productInstances[lastIndex];
        const secondLastProductInstance = productInstances[secondLastIndex];

        const lastProductInstanceIsCopied = lastProductInstance.id === secondLastProductInstance.id;

        const fromDate = productInstances[0].start;

        // under normal circumstances, the end date will be the "start" of the last product instance (because for type "nights" we include the availability for the next day but we're not actually booking that)
        // there's a "hack" where we copy a product instance for type "nights" to be able to select a time range that ends on a day that is closed
        // But in case where the last product instance is a copy, we need to use the "end" of the last product instance instead otherwise we get 1 date too few in the range
        const toDate = !lastProductInstanceIsCopied
            ? lastProductInstance.start
            : lastProductInstance.end;

        return fromDate.format('ll') + ' - ' + toDate.format('ll');
    } else {
        if (cartItem.products[0].timeslots.length > 0) {
            const timeslots = cartItem.products[0].timeslots;
            const start = timeslots[0].start;
            const end = timeslots[timeslots.length - 1].end;
            // Same-day session only needs the end time; a session crossing midnight
            // shows the full end date too.
            const endFormat = start.isSame(end, 'day') ? 'LT' : 'lll';
            return `${start.format('lll')} - ${end.format(endFormat)}`;
        }
        return cartItem.products[0].start.format('lll');
    }
}

export function getBookingClosedItems(cartItems: CartItem[]) {
    return cartItems.filter((item) =>
        item.products.some((product) => product.cutoffDate?.isBefore(TZDate.now())),
    );
}

export function getPackageDuration(
    date: TZDate,
    pkg: Package,
    selectedProducts?: ProductInstance[],
): [TZDate, TZDate] {
    if (selectedProducts) {
        const earliestStart = selectedProducts.reduce(
            (acc, cur) => {
                return !acc || cur.start.isBefore(acc) ? cur.start : acc;
            },
            null as null | TZDate,
        );
        const latestEnd = selectedProducts.reduce(
            (acc, cur) => {
                return !acc || cur.end.isAfter(acc) ? cur.end : acc;
            },
            null as null | TZDate,
        );

        if (earliestStart && latestEnd) {
            return [earliestStart, latestEnd];
        }
    }
    const start = Math.min(
        ...pkg.ticketOptions.flatMap((to) =>
            pkg.products
                .filter((p) => to.products.includes(p.id))
                .map((p) => p.pkgMinutesRelativeStart ?? 0),
        ),
    );
    const end = Math.max(
        ...pkg.ticketOptions.flatMap((to) =>
            pkg.products
                .filter((p) => to.products.includes(p.id))
                .map((p) => p.pkgMinutesRelativeEnd ?? 0),
        ),
    );

    return [date.add(start, 'minutes'), date.add(end, 'minutes')];
}

export function isActivityCartItem(item: CartItem): boolean {
    return !item.pkg && item.products.some(isActivityProductInstance);
}

export function isActivityProductInstance(productInstance: ProductInstance): boolean {
    return isActivityProduct(productInstance.product) && !isExtraProductInstance(productInstance);
}

export function isActivityProduct(product: Product | null): boolean {
    if (!product) return false;
    return ['days', 'timepoint'].includes(product.type);
}

export function isPackageCartItem(item: CartItem): boolean {
    return !!item.pkg;
}

export function isExtraCartItem(item: CartItem): boolean {
    return item.products.length > 0 && item.products.every(isExtraProductInstance);
}

export function isExtraProductInstance(productInstance: ProductInstance): boolean {
    return productInstance.isExtraProduct;
}

export function isTimeslotCartItem(item: CartItem): boolean {
    return item.products.some(isTimeslotProductInstance);
}

export function isTimeslotProductInstance(productInstance: ProductInstance): boolean {
    return isTimeslotProduct(productInstance.product);
}

export function isTimeslotProduct(product: Product | null): boolean {
    if (!product) return false;
    return product.type === 'timeslot';
}

export function isAccomodationOrNightsCartItem(item: CartItem): boolean {
    return !item.pkg && item.products.some(isAccomodationOrNightsProductInstance);
}

export function isAccomodationOrNightsProductInstance(productInstance: ProductInstance): boolean {
    return isAccomodationOrNightsProduct(productInstance.product);
}

export function isAccomodationOrNightsProduct(product: Product | null): boolean {
    if (!product) return false;
    return ['nights', 'accommodation'].includes(product.type);
}

export function isPureAccomodationProduct(product: Product | null): boolean {
    if (!product) return false;
    return product.type === 'accommodation';
}

export function productInstancesDisablePaymentPlans(
    productInstances: ProductInstance[],
    isPackageProduct: boolean,
    configDisableMembershipBooking?: boolean,
): boolean {
    return productInstances.some((productInstance) =>
        productInstanceDisablePaymentPlans(
            productInstance,
            isPackageProduct,
            configDisableMembershipBooking,
        ),
    );
}

export function productInstanceDisablePaymentPlans(
    productInstance: ProductInstance,
    isPackageProduct: boolean,
    configDisableMembershipBooking?: boolean,
): boolean {
    return productDisablePaymentPlans(
        productInstance.product,
        isPackageProduct,
        productInstanceHasDepositPrices(productInstance),
        configDisableMembershipBooking,
    );
}

export function productDisablePaymentPlans(
    product: Product | null,
    isPackageProduct: boolean,
    productInstanceHasDepositPrices: boolean,
    configDisableMembershipBooking?: boolean,
): boolean {
    if (!product) return false;

    return (
        isPureAccomodationProduct(product) ||
        isPackageProduct ||
        !!configDisableMembershipBooking ||
        productHasDepositPrices(product) ||
        productInstanceHasDepositPrices
    );
}

export function cartHasDisabledPaymentPlans(cartItems: CartItem[]) {
    return cartItems.some((item) => item.disablePaymentPlans);
}

export function shouldUsePaymentPlansForCart(
    cartItems: CartItem[],
    currentSite: MembershipSite | null,
    isCompanyContact: boolean,
): boolean {
    if (isCompanyContact) return false;
    if (cartItems.length === 0) return false;

    const nonWaitingListItems = filterBookableItems(cartItems);
    if (nonWaitingListItems.length === 0) return false;

    const isMembershipSite = !!currentSite;

    // Payment plans (membership) apply when the tenant is a membership site and the cart
    // contains no membership-incompatible products (accommodation/package/deposit, which
    // set disablePaymentPlans). Timeslots no longer force payment plans — Base books them
    // via /api/reserve. SB-962.
    return isMembershipSite && !cartHasDisabledPaymentPlans(nonWaitingListItems);
}

export function productInstancesHasDepositPrices(productInstances: ProductInstance[]): boolean {
    return productInstances.some(productInstanceHasDepositPrices);
}

export function productInstanceHasDepositPrices(productInstance: ProductInstance): boolean {
    return productInstance.ticketOptions.some((x) => x.depositAmount && x.depositAmount > 0);
}

export function productHasDepositPrices(product: Product): boolean {
    return product.ticketOptions.some((x) => x.depositAmount && x.depositAmount > 0);
}

export function createNewCartItem(
    selectedProducts: ProductInstance[],
    ticketOptions: TicketOptionWithQuantity[],
    disablePaymentPlans: boolean,
    timeslots?: Timeslot[],
): CartItem {
    return {
        products: selectedProducts.map((p) => ({
            ...p,
            timeslots: timeslots ?? [],
        })),
        ticketOptions: ticketOptions,
        disablePaymentPlans: disablePaymentPlans,
    };
}

export function addMembershipToCartItems(
    cartItems: CartItem[],
    paymentPlanResponse: MembershipIntentMultiResponse,
): CartItem[] {
    return cartItems.map((item) => {
        const ticketOptionsWithPaymentPlans = getTicketOptionsWithPaymentPlans(
            item,
            paymentPlanResponse,
        );

        const newTicketOptions = ticketOptionsWithPaymentPlans.map(
            (ticketOptionWithPaymentPlans) => {
                return {
                    ...ticketOptionWithPaymentPlans.ticketOption,
                    membership: ticketOptionWithPaymentPlans.paymentPlans,
                } as TicketOptionWithQuantity;
            },
        );

        return {
            ...item,
            ticketOptions: newTicketOptions,
        };
    });
}

export function removeMembershipFromCartItems(cartItems: CartItem[]): CartItem[] {
    return cartItems.map((item) => {
        const newTicketOptions = item.ticketOptions.map((ticketOption) => {
            return {
                ...ticketOption,
                membership: undefined,
            } as TicketOptionWithQuantity;
        });

        return {
            ...item,
            ticketOptions: newTicketOptions,
        };
    });
}

export function getCutoffDateFromStartAndCutoffTime(start: TZDate, cutoffTime: number | null) {
    return start.subtract(cutoffTime ?? 0, 'minutes');
}

export function getCutoffDateForProductInstance(productInstance: ProductInstance) {
    return (
        productInstance.cutoffDate ??
        getCutoffDateFromStartAndCutoffTime(productInstance.start, productInstance.cutoffTime)
    );
}

export function getCutoffDateForCartItem(item: CartItem) {
    const productInstanceWithEarliestCutoff = minBy(item.products, (p) =>
        getCutoffDateForProductInstance(p).valueOf(),
    );

    if (productInstanceWithEarliestCutoff) {
        return getCutoffDateForProductInstance(productInstanceWithEarliestCutoff);
    }

    if (item.pkg && item.pkg.date) {
        return item.pkg.date;
    }

    // seems like we should not be able to reach this point.
    // (There should always be a product instance with a start date)
    // But not really sure at the moment if that's the case.
    return undefined;
}

export function hasCutoffDatePassedForCartItem(item: CartItem, now: number) {
    try {
        const cutoffDate = getCutoffDateForCartItem(item);

        if (cutoffDate) {
            return cutoffDate.isBefore(now);
        }
    } catch (e) {
        debugLog('Error in hasCutoffDatePassedForCartItem', e);
        return false; // Really don't like to ignore the error, but this is the current behavior.
    }
}

export function removeInvalidCartItems(cart: CartItem[]) {
    const now = Date.now();
    return cart.filter((item) => {
        return !hasCutoffDatePassedForCartItem(item, now);
    });
}

export function removeRelatedExtras(cart: CartItem[], item: CartItem): CartItem[] {
    if (item.products.some((p) => p.isExtraProduct)) {
        return cart;
    }

    const extraProductIdsToRemove = item.products.flatMap((p) =>
        p.extraProducts.concat(p.timeslots.flatMap((t) => t.extraProducts)),
    );

    if (extraProductIdsToRemove.length === 0) {
        return cart;
    }

    return cart.filter(
        (cartItem) =>
            !cartItem.products.some(
                (p) => p.isExtraProduct && extraProductIdsToRemove.includes(p.id),
            ),
    );
}

export function isWaitingListCartItem(item: CartItem): boolean {
    return (
        item.products?.some((instance) => instance.hasWaitingList && instance.isClosedForBooking) ??
        false
    );
}

export function isWaitingListTicketOption(ticketOption: TicketOptionWithQuantity): boolean {
    return ticketOption.productInstances.some((pi) => pi.hasWaitingList && pi.isClosedForBooking);
}

export function isBookableCartItem(item: CartItem): boolean {
    return !isWaitingListCartItem(item);
}

export function filterWaitingListItems(cartItems: CartItem[]): CartItem[] {
    return cartItems.filter(isWaitingListCartItem);
}

export function filterBookableItems(cartItems: CartItem[]): CartItem[] {
    return cartItems.filter(isBookableCartItem);
}
